/**
 * SentenceBuffer — accumulates streaming text chunks and emits complete sentences.
 *
 * Used by the streaming TTS pipeline to buffer SSE text events into
 * sentence-sized pieces suitable for individual ElevenLabs TTS requests.
 *
 * Key behaviors:
 *   - Detects sentence boundaries: [.!?] followed by whitespace + uppercase
 *   - Skips abbreviations (Dr., Mr., etc.), decimal numbers, URLs
 *   - Force-flushes at MAX_CHUNK chars (long unpunctuated text)
 *   - Timeout-flushes after 800ms silence (tool execution pauses)
 *   - flush() is "soft" — emits remaining text but allows more feed() calls after
 *   - clear() discards without emitting (barge-in)
 */

const MIN_CHUNK = 40;
const MAX_CHUNK = 250;
const FLUSH_TIMEOUT_MS = 800;

const ABBREVIATIONS = /(?:Dr|Mr|Mrs|Ms|Jr|Sr|Prof|Gen|Rep|Sen|Gov|Rev|St|Ave|Blvd|Inc|Corp|Ltd|vs|etc)\./i;

export class SentenceBuffer {
  private buffer = '';
  private pendingPrefix = '';
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private onSentence: (sentence: string) => void;
  // First-sentence-of-turn bypass: short preambles like "Let me look up X."
  // (≈30–40 chars) would otherwise be stashed in pendingPrefix waiting for
  // the next sentence — which only arrives after the tool finishes. We let
  // the first complete sentence through regardless of length so the voice
  // preamble reaches TTS before tool execution starts.
  private firstSentenceEmitted = false;

  constructor(onSentence: (sentence: string) => void) {
    this.onSentence = onSentence;
  }

  feed(text: string): void {
    this.buffer += text;
    this.clearFlushTimer();

    // Extract complete sentences
    let boundary = this.findSentenceBoundary();
    while (boundary !== -1) {
      const raw = this.buffer.slice(0, boundary).trim();
      this.buffer = this.buffer.slice(boundary).trimStart();

      // Combine with any pending prefix from a previous too-short sentence
      const sentence = this.pendingPrefix ? (this.pendingPrefix + ' ' + raw).trim() : raw;
      this.pendingPrefix = '';

      // Merge tiny fragments with the next sentence instead of emitting alone.
      // Store in pendingPrefix rather than re-inserting into buffer to avoid
      // re-finding the same boundary (which would cause an infinite loop).
      // Exception: the FIRST sentence of a turn is allowed through at any
      // length so a short preamble before a tool call is spoken immediately.
      if (sentence.length < MIN_CHUNK && this.firstSentenceEmitted) {
        this.pendingPrefix = sentence;
        boundary = this.findSentenceBoundary();
        continue;
      }

      if (sentence.length > 0) {
        this.emitSentence(sentence);
      }

      boundary = this.findSentenceBoundary();
    }

    // Force flush if buffer (including pending prefix) exceeds max
    const totalBuffered = this.pendingPrefix.length + this.buffer.length;
    if (totalBuffered >= MAX_CHUNK) {
      const combined = this.pendingPrefix
        ? (this.pendingPrefix + ' ' + this.buffer).trim()
        : this.buffer.trim();
      this.pendingPrefix = '';
      this.buffer = '';

      const breakPoint = this.findSoftBreakIn(combined);
      if (breakPoint > MIN_CHUNK) {
        const chunk = combined.slice(0, breakPoint).trim();
        this.buffer = combined.slice(breakPoint).trimStart();
        if (chunk.length > 0) {
          this.emitSentence(chunk);
        }
      } else if (combined.length > 0) {
        this.emitSentence(combined);
      }
    }

    // Arm timeout flush for tool-pause scenarios
    const pending = (this.pendingPrefix + this.buffer).trim();
    if (pending.length > 0) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        this.flush();
      }, FLUSH_TIMEOUT_MS);
    }
  }

  /**
   * Soft flush — emit whatever remains in the buffer.
   * Does NOT mark the stream as done. More feed() calls are allowed after
   * (e.g., text resumes after tool execution).
   */
  flush(): void {
    this.clearFlushTimer();
    const combined = this.pendingPrefix
      ? (this.pendingPrefix + ' ' + this.buffer).trim()
      : this.buffer.trim();
    this.pendingPrefix = '';
    this.buffer = '';
    if (combined.length > 0) {
      this.emitSentence(combined);
    }
  }

  /** Discard buffer without emitting (barge-in). */
  clear(): void {
    this.clearFlushTimer();
    this.buffer = '';
    this.pendingPrefix = '';
  }

  /** Wrap onSentence to prevent exceptions from corrupting buffer state */
  private emitSentence(text: string): void {
    try {
      this.onSentence(text);
    } catch (err) {
      console.warn('[SentenceBuffer] onSentence threw:', err);
    }
    this.firstSentenceEmitted = true;
  }

  private clearFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Find a sentence boundary in the buffer.
   * Returns the index just past the sentence-ending punctuation, or -1.
   */
  private findSentenceBoundary(): number {
    for (let i = 0; i < this.buffer.length - 1; i++) {
      const char = this.buffer[i];
      if (char !== '.' && char !== '!' && char !== '?') continue;

      const nextChar = this.buffer[i + 1];

      if (char === '.') {
        // Skip abbreviations: look at the word ending with this period
        const wordBefore = this.buffer.slice(Math.max(0, i - 10), i + 1);
        if (ABBREVIATIONS.test(wordBefore)) continue;

        // Skip decimal numbers: "3.14"
        if (i > 0 && /\d/.test(this.buffer[i - 1]) && /\d/.test(nextChar)) continue;

        // Skip URLs: "example.com"
        if (i > 0 && /[a-zA-Z]/.test(this.buffer[i - 1]) && /[a-zA-Z]/.test(nextChar)) continue;

        // Skip ellipsis: "..."
        if (nextChar === '.') continue;
      }

      // Sentence boundary: punctuation followed by whitespace
      if (/\s/.test(nextChar)) {
        // Check for uppercase, quote, or newline after whitespace
        const afterSpace = this.buffer[i + 2];
        if (afterSpace && /[A-Z\n"'([]/.test(afterSpace)) {
          return i + 1;
        }
        // Newline right after punctuation
        if (nextChar === '\n') {
          return i + 1;
        }
        // For ! and ?, whitespace alone is enough (almost always a sentence end)
        if (char !== '.') {
          return i + 1;
        }
      }
    }
    return -1;
  }

  /**
   * Find a "soft" break point for long unpunctuated text in the given string.
   * Returns the index just past the last comma, semicolon, or dash.
   */
  private findSoftBreakIn(text: string): number {
    const searchRegion = text.slice(0, MAX_CHUNK);
    const lastComma = searchRegion.lastIndexOf(', ');
    const lastSemicolon = searchRegion.lastIndexOf('; ');
    const lastDash = searchRegion.lastIndexOf(' — ');
    const lastNewline = searchRegion.lastIndexOf('\n');

    // Return the position just after the delimiter
    return Math.max(
      lastComma >= 0 ? lastComma + 1 : -1,
      lastSemicolon >= 0 ? lastSemicolon + 1 : -1,
      lastDash >= 0 ? lastDash + 3 : -1,  // ' — ' is 3 chars
      lastNewline >= 0 ? lastNewline + 1 : -1,
    );
  }
}
